import type {
  SalesDeadlineKind,
  SalesOrgLeadDetail,
} from "@xop/dashboard-shared";

/**
 * Pure lead-quality logic for the lead detail page's "why this is confirmed +
 * passed every quality check" panel. Kept framework-free so it's unit-testable
 * without rendering (see quality.test.ts).
 *
 * The data comes from two places:
 *   - the agent that WROTE the lead (confidence, signals, persons, bundle), and
 *   - the independent lead-quality JUDGE (`eval_grounding` / `eval_fit` /
 *     `eval_verdict` / `eval_reason`), which is `null` until a lead is scored.
 *
 * Every check degrades gracefully: an unscored lead still shows truthful
 * structural checks (it has sources, it clears the confidence floor, it has a
 * contact) instead of a fake score.
 */

export type Tone = "good" | "warn" | "bad" | "neutral";

/** MUI palette path per tone — used for sx `color` / `bgcolor`. */
export const TONE_COLOR: Record<Tone, string> = {
  good: "success.main",
  warn: "warning.main",
  bad: "error.main",
  neutral: "text.disabled",
};

export interface ScoreBand {
  /** One-word strength label for a 0..1 score. */
  label: string;
  tone: Tone;
}

export function scoreBand(score: number | null | undefined): ScoreBand {
  if (score == null) return { label: "—", tone: "neutral" };
  if (score >= 0.8) return { label: "Strong", tone: "good" };
  if (score >= 0.6) return { label: "Solid", tone: "good" };
  if (score >= 0.4) return { label: "Moderate", tone: "warn" };
  return { label: "Weak", tone: "bad" };
}

export interface VerdictMeta {
  label: string;
  tone: Tone;
  icon: string;
  blurb: string;
}

/** The headline confirmation badge derived from the judge's verdict. */
export function verdictMeta(
  verdict: SalesOrgLeadDetail["eval_verdict"] | undefined,
): VerdictMeta {
  switch (verdict) {
    case "keep":
      return {
        label: "Confirmed",
        tone: "good",
        icon: "✓",
        blurb: "Passed the independent quality review",
      };
    case "review":
      return {
        label: "Worth a look",
        tone: "warn",
        icon: "◐",
        blurb: "The quality review flagged this for a human check",
      };
    case "reject":
      return {
        label: "Quality flag",
        tone: "bad",
        icon: "!",
        blurb: "The quality review raised concerns about this lead",
      };
    default:
      return {
        label: "Review pending",
        tone: "neutral",
        icon: "○",
        blurb: "Not yet scored by the quality judge — structural checks below",
      };
  }
}

export interface QualityCheck {
  key: string;
  label: string;
  detail: string;
  state: "pass" | "warn" | "fail";
}

export interface RfpDeadlineMeta {
  /** The raw ISO `YYYY-MM-DD`. */
  iso: string;
  /** Locale-formatted date, e.g. "Apr 6, 2026". */
  label: string;
  /** Which kind of dated window this is, so callers can theme/caption it. */
  kind: SalesDeadlineKind;
  /** Server verdict — the bid window has already closed. */
  expired: boolean;
  /** Signed days to the deadline (negative = already passed). */
  daysAway: number;
  tone: Tone;
  /** Compact pill label, e.g. "RFP closed" / "RFP due soon" / "Event ends". */
  chip: string;
  /** Full one-liner, e.g. "RFP closed Apr 6, 2026 · 61 days ago". */
  text: string;
}

/** Per-kind wording for the deadline pill — `noun` + the open/closed verb. The
 * RFP wording is preserved verbatim ("RFP due" / "RFP due soon" / "RFP closed")
 * so the long-standing RFP pill + its tests don't move. */
const DEADLINE_WORDS: Record<
  SalesDeadlineKind,
  { noun: string; openVerb: string; closedVerb: string }
> = {
  rfp: { noun: "RFP", openVerb: "due", closedVerb: "closed" },
  event: { noun: "Event", openVerb: "ends", closedVerb: "ended" },
  contract_end: { noun: "Contract", openVerb: "expires", closedVerb: "expired" },
  eol: { noun: "EOL", openVerb: "due", closedVerb: "passed" },
  registration: { noun: "Registration", openVerb: "closes", closedVerb: "closed" },
  other: { noun: "Deadline", openVerb: "due", closedVerb: "passed" },
};

/** Human caption for a deadline kind, e.g. for a badge tooltip. */
export function deadlineKindLabel(
  kind: SalesDeadlineKind | null | undefined,
  fallbackLabel?: string | null,
): string {
  if (fallbackLabel && fallbackLabel.trim()) return fallbackLabel.trim();
  return DEADLINE_WORDS[kind ?? "other"]?.noun ?? "Deadline";
}

/**
 * The dated buying-window deadline shown on a lead — the answer to the
 * operator's "why didn't the AI count the due date?". Generalized beyond RFP to
 * ANY dated trigger (an ending event, a contract / EOL date, a registration
 * cutoff): it reads the generic `deadline` + `deadline_kind`, falling back to
 * the legacy `rfp_deadline` (kind `rfp`) for rows not yet re-serialized. Pure +
 * framework-free so it's unit-testable and the page just renders it.
 *
 * Returns `null` when the lead carries no deadline. An EXPIRED window reads as a
 * caution (`warn`), never a failure — the lead keeps all its value, the agent
 * just cooled its heat. `now` is injectable for deterministic tests.
 */
export function deadlineMeta(
  lead: Pick<
    SalesOrgLeadDetail,
    "rfp_deadline" | "rfp_expired" | "deadline" | "deadline_kind" | "deadline_label"
  >,
  now: Date = new Date(),
): RfpDeadlineMeta | null {
  // The generic `deadline` is canonical; fall back to the legacy rfp field.
  const generic = (lead.deadline ?? "").trim();
  const raw = generic || (lead.rfp_deadline ?? "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!m) return null;
  const kind: SalesDeadlineKind = generic
    ? lead.deadline_kind ?? "other"
    : "rfp";
  const words = DEADLINE_WORDS[kind] ?? DEADLINE_WORDS.other;

  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // Compare date-only at local noon so a same-day deadline never tips over a
  // timezone/DST boundary into "yesterday".
  const deadline = new Date(y, mo - 1, d, 12, 0, 0, 0);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
  const daysAway = Math.round((deadline.getTime() - today.getTime()) / 86_400_000);
  // The server's `rfp_expired` verdict is only meaningful for an RFP deadline;
  // for any other kind, derive expiry locally.
  const expired = (kind === "rfp" ? lead.rfp_expired : undefined) ?? daysAway < 0;
  const label = deadline.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  if (expired) {
    const ago = Math.abs(daysAway);
    return {
      iso: raw.slice(0, 10),
      label,
      kind,
      expired: true,
      daysAway,
      tone: "warn",
      chip: `${words.noun} ${words.closedVerb}`,
      text: `${words.noun} ${words.closedVerb} ${label} · ${ago} day${ago === 1 ? "" : "s"} ago`,
    };
  }
  const soon = daysAway <= 14;
  const whenText =
    daysAway === 0
      ? `${words.openVerb} today`
      : `${words.openVerb} in ${daysAway} day${daysAway === 1 ? "" : "s"}`;
  return {
    iso: raw.slice(0, 10),
    label,
    kind,
    expired: false,
    daysAway,
    tone: soon ? "warn" : "good",
    chip: soon ? `${words.noun} ${words.openVerb} soon` : `${words.noun} ${words.openVerb}`,
    text: `${words.noun} ${whenText} · ${label}`,
  };
}

/**
 * Back-compat alias — the lead page + existing tests call `rfpDeadlineMeta`.
 * It now delegates to the generalized {@link deadlineMeta}, which reads the
 * generic `deadline` and falls back to `rfp_deadline`.
 */
export function rfpDeadlineMeta(
  lead: Pick<
    SalesOrgLeadDetail,
    "rfp_deadline" | "rfp_expired" | "deadline" | "deadline_kind" | "deadline_label"
  >,
  now: Date = new Date(),
): RfpDeadlineMeta | null {
  return deadlineMeta(lead, now);
}

/** Count of cited sources behind the lead (signals + brief-pinned evidence). */
export function evidenceCount(lead: SalesOrgLeadDetail): number {
  return (lead.signals?.length ?? 0) + (lead.briefs?.[0]?.evidence?.length ?? 0);
}

/**
 * The checklist of why a lead qualified. Each row is a single, plain-English
 * reason a non-technical operator can read top-to-bottom. Scores from the judge
 * win when present; otherwise we fall back to structural evidence so the row is
 * still truthful (never a fabricated pass).
 */
export function qualityChecks(lead: SalesOrgLeadDetail): QualityCheck[] {
  const sources = evidenceCount(lead);
  const persons = lead.persons ?? [];
  const firstPerson = persons[0];
  const hasEmail = persons.some((p) => !!p.email);
  const contactPath = lead.briefs?.[0]?.suggested_contact_path ?? "";
  const floor = lead.confidence_floor ?? 0;
  const confPct = Math.round((lead.confidence ?? 0) * 100);
  const floorPct = Math.round(floor * 100);

  const grounding = lead.eval_grounding ?? (sources > 0 ? 0.7 : 0);
  const fit = lead.eval_fit ?? (lead.vertical ? 0.7 : 0);
  // A solution-led FIT lead has an evergreen operational match, not a dated
  // buying event — so the "need" + "trigger" rows read differently (the same
  // hard grounding/fit/need floors still apply).
  const isFit = lead.lead_kind === "fit";

  return [
    {
      key: "grounded",
      label: "Grounded in public evidence",
      detail:
        sources > 0
          ? `Cites ${sources} public source${sources === 1 ? "" : "s"} naming ${lead.name}`
          : "No cited source yet",
      state: grounding >= 0.5 ? "pass" : "fail",
    },
    {
      key: "fit",
      label: "Fits an XOP product",
      detail:
        [lead.vertical, lead.recommended_bundle].filter(Boolean).join(" · ") ||
        "vertical not set",
      state: fit >= 0.5 ? "pass" : "warn",
    },
    {
      key: "need",
      label: isFit ? "Has a real, evidenced need" : "Has a current, provable need",
      detail:
        lead.eval_need != null
          ? lead.eval_need >= 0.6
            ? isFit
              ? "A specific, evidenced operational workflow an XOP product serves"
              : "A dated public buying trigger (RFP / EOL / incident / regulation)"
            : isFit
              ? "Only a generic fit — no specific evidenced workflow"
              : "Topical fit only — no current trigger confirmed"
          : "Pending the quality review",
      state: lead.eval_need != null ? (lead.eval_need >= 0.6 ? "pass" : "warn") : "warn",
    },
    {
      key: "confidence",
      label: "Above the confidence bar",
      detail:
        floor > 0
          ? `${confPct}% — clears the ${floorPct}% minimum`
          : `${confPct}% agent confidence`,
      state: (lead.confidence ?? 0) >= floor ? "pass" : "warn",
    },
    {
      key: "evidence",
      label: isFit ? "Backed by evidence on file" : "Has a dated trigger event",
      detail:
        sources > 0
          ? `${sources} evidence item${sources === 1 ? "" : "s"} on file`
          : "no evidence attached",
      state: sources > 0 ? "pass" : "fail",
    },
    {
      key: "contact",
      label: "Reachable decision-maker",
      detail: firstPerson
        ? `${firstPerson.full_name}${hasEmail ? " · email on file" : ""}`
        : contactPath
          ? "contact path suggested"
          : "no contact surfaced yet",
      state: firstPerson ? (hasEmail ? "pass" : "warn") : contactPath ? "warn" : "fail",
    },
  ];
}

/** How many checks passed — drives the "5/5 checks passed" summary line. */
export function passSummary(checks: QualityCheck[]): { passed: number; total: number } {
  return { passed: checks.filter((c) => c.state === "pass").length, total: checks.length };
}
